import { useEffect, useState } from "react";
import { Monitor, Download, X, Volume2 } from "lucide-react";
import { api, API } from "@/lib/api";

const DISMISS_KEY = "ww_desktop_banner_dismissed";
const isElectron = typeof navigator !== "undefined" && /electron/i.test(navigator.userAgent || "");

// Promotes the native desktop app (always-on-audio coaching over fullscreen games).
// Visible only to browser users when the owner has enabled it in the portal.
export function DesktopBanner() {
    const [cfg, setCfg] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
    });

    useEffect(() => {
        if (isElectron) return;
        api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
    }, []);

    if (isElectron || dismissed || !cfg?.desktop_banner_enabled) return null;

    const dismiss = () => {
        setDismissed(true);
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    };

    const downloadUrl = cfg.desktop_download_url || (cfg.desktop_available ? `${API}/desktop/download` : "");
    const hasUrl = !!downloadUrl;

    return (
        <div data-testid="desktop-banner"
            className="relative mb-6 rounded-sm border border-volt/40 bg-gradient-to-r from-volt/[0.10] to-transparent p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="grid place-items-center w-11 h-11 rounded-sm bg-volt/15 border border-volt/40 shrink-0">
                <Monitor className="w-5 h-5 text-volt" />
            </div>
            <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-white flex items-center gap-2">
                    Coaching cuts out in fullscreen? Get the Desktop App
                </h3>
                <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <Volume2 className="w-3.5 h-3.5 text-volt shrink-0" />
                    Always-on voice coaching over your fullscreen game, native capture &amp; auto game detection.
                </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {hasUrl ? (
                    <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="desktop-banner-download"
                        className="h-10 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] transition-colors flex items-center gap-2">
                        <Download className="w-4 h-4" /> Download
                    </a>
                ) : (
                    <span data-testid="desktop-banner-soon"
                        className="h-10 px-5 rounded-sm border border-border text-muted-foreground font-semibold flex items-center gap-2 cursor-default">
                        <Download className="w-4 h-4" /> Coming soon
                    </span>
                )}
                <button onClick={dismiss} data-testid="desktop-banner-dismiss" title="Dismiss"
                    className="grid place-items-center w-9 h-9 rounded-sm border border-border text-muted-foreground hover:text-white hover:bg-white/5">
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
